/**
 * THE TWO LENSES, as SVG filters the stylesheet points at by id (`ui/glass.css`).
 *
 * WHAT A DISPLACEMENT MAP IS HERE: an image whose red channel says how far each pixel of
 * the backdrop moves horizontally and whose green channel says how far it moves vertically,
 * with 128 meaning "stay". `preserveAspectRatio="none"` with a percentage subregion is what
 * lets ONE map serve a 260px sidebar and a 36px capsule: the map stretches to the surface,
 * so the profile is a proportion of the surface rather than a fixed number of pixels.
 *
 * ROUND 2 (2026-09-11) REPLACED THE RAMP WITH A SURFACE PROFILE. Round 1's map was two
 * LINEAR ramps over the outer 18 percent of each axis and neutral everywhere else, which is
 * a bevel and not a lens: it bends only in a band a handful of pixels wide on a capsule, and
 * it bends by the same amount per pixel all the way across that band. The published method
 * is a map generated from the SURFACE the light passes through, sampled along the radius -
 * "127 samples along the radius, polar to cartesian, r = 128 + x * 127, g = 128 + y * 127"
 * (web: https://kube.io/blog/liquid-glass-css-svg/). `PROFILE` below is that surface, taken
 * per axis so one map can still stretch to every surface the product has:
 *
 *   the glass is a convex cap; a ray hitting it at the fraction `u` of the half-axis leaves
 *   deflected by the surface's slope there, and the slope of a circular cap is
 *   `u / sqrt(1 - (u * CURVATURE)^2)`, normalised so the rim is 1.
 *
 * That is flat under the middle of a surface, grows slowly across it and is steepest at the
 * rim, which is where a real lens concentrates light: "this new set of materials dynamically
 * bends, shapes, and concentrates light in real time" (wwdc: WWDC25 219, "Meet Liquid
 * Glass"). `CURVATURE` is how near the cap's own edge the profile is cut off; at 1 the slope
 * is infinite at the rim and the map saturates over half the surface.
 *
 * THE BEND POINTS INWARD, which is both what a convex lens does and the only direction that
 * cannot run out of backdrop. A magnifier shows the point at radius r as it is at r/M, so
 * the sample moves TOWARD the middle: at the left rim the map reads above 128 (sample from
 * the right) and at the right rim below it. Round 1's map had the sign the other way, so
 * every rim sampled from beyond the surface's own backdrop region.
 *
 * THE BLUR IS NOT IN HERE AND IT HAPPENS FIRST. "Blur the source first, then displace" is
 * the published order (web: https://blog.logrocket.com/how-create-liquid-glass-effects-css-and-svg/,
 * which runs `feGaussianBlur` on the source before `feDisplacementMap scale="55"`); round 1
 * displaced by at most 4.5px and then blurred by 6px in the same declaration, which is a
 * low-pass filter over the whole effect. The blur radius is per surface family and per
 * appearance and an SVG attribute cannot read a custom property, so the order is made in the
 * CSS declaration instead - `backdrop-filter: <the kit's blur> url(#lens)` in `ui/glass.css`
 * - and the filter here only bends what reaches it.
 *
 * THE MAP IS A data: URI AND THAT IS A CHROMIUM-ONLY LUXURY. Measured 2026-09-10 in both
 * engines: `feImage` resolves a data: URI in Chromium and does NOT in WebKit, where the
 * whole filter then becomes invalid and the element it is on is not rendered at all - the
 * probe capsule vanished. Only the refraction path uses these filters and only Chromium is
 * on it, so a data: URI is correct here; anything that ever puts a filter on a WebKit
 * surface has to serve the map as a FILE (web: https://aave.com/design/building-glass-for-the-web,
 * "a small PNG we build on the fly") and must not put it on anything carrying text, because
 * an invalid filter removes its element and this product's floor is legibility.
 *
 * `colorInterpolationFilters="sRGB"` is not decoration: the default is linearRGB, which
 * would put the map's own 128 somewhere else and bend the middle of every surface.
 *
 * THE SCALES COME FROM `tokens.css` (`--glass-lens-scale-pane`, `--glass-lens-scale-control`)
 * and are repeated here as numbers because `scale` is an SVG attribute and cannot read a
 * custom property. `ui/glass.test.ts` reads both places off disk and fails if they drift.
 *
 * WAVE 4 ROUND 3 TOOK THEM OFF THE WEB ARTICLES AND OUT OF THE KIT. The kit publishes the
 * lens as a distortion and a depth per size (0.4 and 0.8 for `Small UI`, 0.3 and 0.9 for
 * `Medium UI`), which are fractions of the shape and not pixels; the derivation from those
 * two numbers to this `scale` is written once, in `tokens.css` beside the tokens, and it
 * multiplies out to 15.36 for a capsule and 54 for a pane. What the two numbers below are
 * NOT any more is 55 and half of 55.
 */
export const GLASS_LENS_SCALE = { pane: 54, control: 15.36 } as const;

/** How near the cap's own edge the profile is cut off; see the header. */
const CURVATURE = 0.95;

/** Samples per half-axis. Seventeen stops per side is below the eye's step on a 260px pane. */
const SAMPLES = 16;

/** The convex cap's slope along one axis, normalised so the rim is exactly 1. */
function profile(u: number): number {
  const slope = (value: number): number => value / Math.sqrt(1 - (value * CURVATURE) ** 2);
  return slope(u) / slope(1);
}

/**
 * One axis of the map as a gradient: 128 in the middle, rising toward one rim and falling
 * toward the other, with the profile's shape rather than a straight line between them.
 */
function axis(id: string, channel: 'r' | 'g', horizontal: boolean): string {
  const stops: string[] = [];
  for (let step = -SAMPLES; step <= SAMPLES; step += 1) {
    const offset = (step + SAMPLES) / (2 * SAMPLES);
    // Negative `step` is the leading rim, which samples from the far side: above 128.
    const value = Math.round(128 - Math.sign(step) * 127 * profile(Math.abs(step) / SAMPLES));
    const colour = channel === 'r' ? `rgb(${value},0,0)` : `rgb(0,${value},0)`;
    stops.push(`<stop offset="${offset.toFixed(4)}" stop-color="${colour}"/>`);
  }
  return (
    `<linearGradient id="${id}" x1="0" y1="0" x2="${horizontal ? 1 : 0}" y2="${horizontal ? 0 : 1}">` +
    `${stops.join('')}</linearGradient>`
  );
}

const MAP_URL = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">` +
    `<defs>${axis('x', 'r', true)}${axis('y', 'g', false)}</defs>` +
    // The two profiles are combined with `screen`, which leaves each channel alone where the
    // other is 0: red keeps the horizontal profile, green the vertical one.
    `<rect width="100" height="100" fill="url(#x)"/>` +
    `<rect width="100" height="100" fill="url(#y)" style="mix-blend-mode:screen"/>` +
    `</svg>`,
)}`;

function Lens({ id, scale }: { readonly id: string; readonly scale: number }) {
  return (
    <filter id={id} x="0" y="0" width="100%" height="100%" colorInterpolationFilters="sRGB">
      <feImage
        href={MAP_URL}
        preserveAspectRatio="none"
        x="0"
        y="0"
        width="100%"
        height="100%"
        result="lens-map"
      />
      <feDisplacementMap
        in="SourceGraphic"
        in2="lens-map"
        scale={scale}
        xChannelSelector="R"
        yChannelSelector="G"
      />
    </filter>
  );
}

/**
 * Mounted once, at the root of the app, so every chrome surface on every screen can point
 * at the same two filters. It paints nothing itself.
 */
export function GlassFilters() {
  return (
    // No <title>: the element is `aria-hidden` and names nothing a reader needs, and React
    // 19 treats a <title> as document metadata wherever it finds one.
    <svg className="glass-filters" aria-hidden="true" focusable="false">
      <defs>
        <Lens id="glass-lens-pane" scale={GLASS_LENS_SCALE.pane} />
        <Lens id="glass-lens-control" scale={GLASS_LENS_SCALE.control} />
      </defs>
    </svg>
  );
}
